import { httpClient } from './http-client';

/**
 * Response shape from the dashboard layout API endpoints.
 */
interface DashboardLayoutResponse {
  id?: string;
  layout: string;
  is_default: boolean;
  updated_at?: string;
}

/**
 * Parsed layout configuration stored in the backend.
 * Controls which widgets are visible and their display order.
 */
export interface DashboardLayoutConfig {
  visibleWidgets: string[];
  order: string[];
}

/**
 * Service for managing the customizable dashboard layout.
 *
 * Communicates with the backend dashboard layout endpoints to load,
 * save, and reset the user's preferred widget configuration.
 */
export class DashboardLayoutService {
  private static readonly BASE_URL = '/dashboard';

  /**
   * Fetch the current dashboard layout from the backend.
   *
   * Returns:
   *   DashboardLayoutResponse: The layout configuration including
   *   the JSON string, default flag, and metadata.
   */
  static async getLayout(): Promise<DashboardLayoutResponse> {
    return httpClient.get<DashboardLayoutResponse>(
      `${DashboardLayoutService.BASE_URL}/layout`
    );
  }

  /**
   * Save a new dashboard layout to the backend.
   *
   * Args:
   *   layout (string): JSON-serialized DashboardLayoutConfig.
   *
   * Returns:
   *   DashboardLayoutResponse: The saved layout with updated metadata.
   */
  static async saveLayout(layout: string): Promise<DashboardLayoutResponse> {
    return httpClient.put<DashboardLayoutResponse>(
      `${DashboardLayoutService.BASE_URL}/layout`,
      { layout }
    );
  }

  /**
   * Reset the dashboard layout to the server-side default.
   *
   * Returns:
   *   DashboardLayoutResponse: The default layout configuration.
   */
  static async resetLayout(): Promise<DashboardLayoutResponse> {
    return httpClient.post<DashboardLayoutResponse>(
      `${DashboardLayoutService.BASE_URL}/layout/reset`
    );
  }

  /**
   * Parse the layout JSON string into a typed config object.
   *
   * Args:
   *   layoutJson (string): Raw JSON string from the backend.
   *
   * Returns:
   *   DashboardLayoutConfig | null: Parsed config, or null if parsing fails.
   */
  static parseLayout(layoutJson: string): DashboardLayoutConfig | null {
    try {
      const parsed = JSON.parse(layoutJson);
      // Reason: Validate that parsed object has the expected shape before returning.
      if (
        parsed &&
        Array.isArray(parsed.visibleWidgets) &&
        Array.isArray(parsed.order)
      ) {
        return parsed as DashboardLayoutConfig;
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Serialize a layout config object into a JSON string for storage.
   *
   * Args:
   *   config (DashboardLayoutConfig): The layout configuration to serialize.
   *
   * Returns:
   *   string: JSON-serialized layout string.
   */
  static serializeLayout(config: DashboardLayoutConfig): string {
    return JSON.stringify(config);
  }
}
